"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../../atoms/button/button";
import styles from "./legal-modal.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * The return-to-top control. Split out because it is the one piece driven by
 * runtime measurement: whether the reader has moved is not knowable before
 * layout, and no native element carries a "scrolled away from the top" state.
 *
 * ⚠ It finds its anchor by `anchorId` rather than by ref, so the two files
 * share nothing but a string.
 *
 * The anchor is the SENTINEL only — it answers "has the reader moved". Going
 * back means resetting the scroll region, not scrolling to the anchor, which
 * sits below the index. See `scrollPortOf`.
 */

export interface LegalModalBackToTopProps {
  /** Id of the server-rendered anchor at the top of the content. */
  anchorId: string;
  /** Visible wording. */
  label: string;
  /**
   * Where the click files in reporting. Without it the atom reports against
   * the page body instead of the index.
   */
  linkPosition?: string;
}

/**
 * ⚠ FIXED, and not the label. The wording is a CMS field, so reporting it
 * would rename the figure whenever an author rewords the button. This is also
 * the value the production component sends, which keeps the two comparable.
 */
const BACK_TO_TOP_LINK_NAME = "toc_back_to_top";

/**
 * The frame's `up control` glyph, path data as exported. Decorative — the
 * label names the control — and sized by Button's icon box.
 */
function UpControlIcon() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      height="100%"
      viewBox="0 0 20 20"
      width="100%"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.745 12.0015C16.0115 12.268 16.0114 12.7006 15.745 12.9672L15.6929 13.0144C15.4429 13.2179 15.0821 13.2177 14.8319 13.0139L14.7798 12.9672L9.72234 7.90914L4.66483 12.9667C4.41495 13.2165 4.01935 13.2319 3.7512 13.0133L3.69912 12.9667C3.44967 12.7169 3.43394 12.3217 3.65246 12.0536L3.69912 12.0015L9.22429 6.47631C9.36046 6.34027 9.53912 6.27656 9.71366 6.27828L9.71312 6.27774C9.7199 6.27751 9.72694 6.27778 9.73373 6.27774C9.90761 6.27681 10.0848 6.34145 10.2198 6.47631L15.745 12.0015Z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * The nearest ancestor that actually scrolls — the Modal's own `.body`.
 *
 * ⚠ Returning to the top means resetting THAT, not scrolling a node into view:
 * the anchor sits below the index, so `scrollIntoView` on it pushes the index
 * out of the region instead of bringing the reader back to it.
 *
 * ⚠ Unreachable from jsdom, which reports every size as 0 and resolves no
 * module CSS, so no spec can exercise the walk itself.
 */
export function scrollPortOf(node: Element | null): Element | null {
  for (let parent = node?.parentElement; parent; parent = parent.parentElement) {
    const { overflowY } = getComputedStyle(parent);
    const scrolls = overflowY === "auto" || overflowY === "scroll";
    if (scrolls && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }
  }
  return null;
}

function reducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function LegalModalBackToTopImpl({
  anchorId,
  label,
  linkPosition
}: LegalModalBackToTopProps) {
  const [away, setAway] = useState(false);
  /* Re-read on click rather than held from the effect: the dialog's content is
     replaced between openings, so a node captured once goes stale. */
  const anchorIdRef = useRef(anchorId);
  anchorIdRef.current = anchorId;

  useEffect(() => {
    const anchor = document.getElementById(anchorId);
    /* ⚠ Guarded because jsdom ships no IntersectionObserver, so a consumer's
       test would throw on mount. Not a browser-support guard. */
    if (!anchor || typeof IntersectionObserver !== "function") {
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => setAway(entries.some(entry => !entry.isIntersecting)),
      { threshold: 0 }
    );
    observer.observe(anchor);

    return () => observer.disconnect();
  }, [anchorId]);

  const toTop = useCallback(() => {
    const anchor = document.getElementById(anchorIdRef.current);
    const behavior = reducedMotion() ? "auto" : "smooth";
    const port = scrollPortOf(anchor);

    if (port) {
      port.scrollTo({
        top: 0,
        behavior
      });
      return;
    }

    /* Nothing scrolls above the anchor, so there is nowhere to return to —
       bring it into view and let the browser decide. */
    anchor?.scrollIntoView({
      behavior,
      block: "start"
    });
  }, []);

  if (!away) {
    return null;
  }

  return (
    <div className={styles.backToTop}>
      {/* ⚠ `small` (36px) is the tablet and mobile frames' own size, under
          this system's 44px floor but clear of WCAG 2.5.8's 24px. The module
          CSS lifts it to `medium` at desktop. */}
      {/* ⚠ `linkName` is what makes the atom push, and nothing else reports
          this click: a plain button outside the close row's form, so neither
          GTM's link-click trigger (anchors only) nor its form-submit trigger
          sees it. Unlike the close control, a push here is the ONLY report
          rather than a second one. */}
      <Button
        className={styles.backToTopButton}
        variant="secondary"
        size="small"
        startIcon={<UpControlIcon />}
        linkName={BACK_TO_TOP_LINK_NAME}
        linkPosition={linkPosition}
        onClick={toTop}
      >
        {label}
      </Button>
    </div>
  );
}
export const LegalModalBackToTop = withSafeRender(LegalModalBackToTopImpl, "LegalModalBackToTop");

export default LegalModalBackToTop;
